import { string } from 'prop-types';
import { last, path, propOr } from 'ramda';
import React, { FC, useCallback, useState } from 'react';
import { compose, setDisplayName, setPropTypes } from 'recompose';
import { ExternalSource } from '../../../../constants/Source';
import { withFolderPath } from '../../../../utils/hoc/withFolderPath';
import { withItemsLoader } from '../../../../utils/hoc/withItemsLoader';
import { withItemsReducer } from '../../../../utils/hoc/withItemsReducer';
import { withMediaManagerSDK } from '../../../../utils/hoc/withMediaManagerSDK';
import { withMediaTypes } from '../../../../utils/hoc/withMediaTypes';
import { withSources } from '../../../../utils/hoc/withSources';
import { IMediaManagerSDKRequestOptions, MediaManagerSDKFolder } from '../../../../utils/media-manager-sdk';
import { decodeWixSiteFolderId, isSiteFilesRootFolderId } from '../../../../utils/media-manager-sdk/items';
import { ExternalSourcesConfigs } from '../../constants/Sources';
import { IInner, IOuter } from './Connected.types';

export const withPropsForFolderPath = (BaseComponent) => {
  const WithPropsForFolderPath: FC<{ source: ExternalSource; folderPath: any }> = (props) => {
    const { folderPath } = props;
    const lastFolderId = folderPath ? propOr(null, 'id', last(folderPath)) : null;
    const [siteFolder, setSiteFolder] = useState(folderPath && folderPath[1]);
    const [folderId, setFolderId] = useState(lastFolderId);
    const [searchQuery, setSearchQuery] = useState('');
    const clearSearch = useCallback(() => setSearchQuery(''), []);

    return (
      <BaseComponent
        {...props}
        {...{ folderId, siteFolder, setSiteFolder, searchQuery, setSearchQuery, clearSearch }}
        navigateToFolder={setFolderId}
      />
    );
  };

  return WithPropsForFolderPath;
};

export const enhance = compose<IInner, IOuter>(
  setPropTypes({
    fileType: string.isRequired,
  }),
  setDisplayName('ConnectedEnhancers'),
  withMediaTypes,
  withSources,
  withPropsForFolderPath,
  withFolderPath({
    validateFolderIdIfCanGetFolderInfo: (folderId, props) => {
      const siteFolder = propOr<any, MediaManagerSDKFolder>(null, 'siteFolder', props);
      const decodedFolderId = decodeWixSiteFolderId(folderId);
      const isItSiteRootFolder = decodedFolderId && isSiteFilesRootFolderId(decodedFolderId.id);

      return { isValid: !isItSiteRootFolder, fallbackFolder: isItSiteRootFolder ? siteFolder : null };
    },
  }),
  withMediaManagerSDK(),
  withItemsLoader(
    ({ source, dynamicLoaderOptions, searchQuery, isWixSitesSource, mediaType }) => ({
      loaderOptions: {
        searchQuery,
        ...path<IMediaManagerSDKRequestOptions>([source, 'loaderOptions'], ExternalSourcesConfigs),
        ...(isWixSitesSource ? { mediaType } : {}),
        ...dynamicLoaderOptions,
      },
    }),
    ['source', 'dynamicLoaderOptions', 'searchQuery', 'isWixSitesSource', 'mediaType'],
  ),
  withItemsReducer,
);
